// "My Cosmetics" panel for the Profile page. Shows owned items grouped by
// category with one-tap equip + a "Browse all" CTA that opens the full
// CosmeticsModal. Skips empty categories so the panel stays compact for new
// players who haven't bought anything yet.
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { CardBack, SKIN_LABELS } from "./CardBack";
import AvatarFrame from "./AvatarFrame";
import CosmeticsModal from "./CosmeticsModal";
import { fetchCosmeticsCatalog, equipCosmetic } from "../lib/cosmetics";
import { sfx } from "../lib/sound";
import { getInitials } from "../lib/avatar";

const SECTIONS = [
  { key: "skins", label: "Card Backs", kind: "skin" },
  { key: "victory_anims", label: "Victory FX", kind: "victory_anim" },
  { key: "avatar_frames", label: "Avatar Frames", kind: "avatar_frame" },
];

function PreviewTile({ kind, item, profileName, isActive, busy, onClick, dataTestId }) {
  const w = 64, h = 84;
  let body;
  if (kind === "skin") {
    body = <div style={{ width: w, height: h }}><CardBack skin={item.id} color="#FFD700" suit="♣" size="md" /></div>;
  } else if (kind === "victory_anim") {
    body = (
      <div className="flex items-center justify-center rounded-lg" style={{
        width: w, height: h,
        background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(0,229,255,0.08))",
        border: "1px solid rgba(255,255,255,0.10)",
      }}>
        <span style={{ fontSize: 26 }}>{
          item.id === "fireworks" ? "🎆" : item.id === "lightning" ? "⚡" : "🎉"
        }</span>
      </div>
    );
  } else {
    body = (
      <div className="flex items-center justify-center" style={{ width: w, height: h }}>
        <AvatarFrame frame={item.id} size={48}>
          <div className="flex items-center justify-center w-full h-full font-display font-black"
            style={{ background: "linear-gradient(155deg, #FFD700, #00E5FF, #B537F2)", color: "#0B0C10",
                     fontSize: 15, letterSpacing: 0.5 }}>
            {getInitials(profileName || "P")}
          </div>
        </AvatarFrame>
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="rounded-2xl p-2 flex flex-col items-center gap-1.5"
      style={{
        background: isActive ? "rgba(255,215,0,0.12)" : "rgba(255,255,255,0.04)",
        border: `1.5px solid ${isActive ? "rgba(255,215,0,0.7)" : "rgba(255,255,255,0.10)"}`,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.6 : 1,
        minWidth: 92,
      }}
      data-testid={dataTestId}
    >
      {body}
      <div className="font-display font-bold text-[11px] text-center leading-tight whitespace-nowrap max-w-full overflow-hidden text-ellipsis">
        {item.name || SKIN_LABELS[item.id] || item.id}
      </div>
      {isActive ? (
        <span className="text-[9px]" style={{ color: "#FFD700" }}>✓ EQUIPPED</span>
      ) : (
        <span className="text-[9px] text-white/55">Tap to equip</span>
      )}
    </button>
  );
}

export default function MyCosmeticsPanel({ profile, onProfileChange }) {
  const [catalog, setCatalog] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("skins");

  const refresh = useCallback(async () => {
    try { setCatalog(await fetchCosmeticsCatalog()); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const handleEquip = async (kind, itemId) => {
    if (busyId) return;
    setBusyId(itemId);
    try {
      const updated = await equipCosmetic(kind, itemId);
      sfx.click();
      onProfileChange?.(updated);
      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId(null);
    }
  };

  const openBrowser = (tab) => {
    setModalTab(tab);
    setModalOpen(true);
  };

  if (!catalog) {
    return (
      <div className="glass rounded-3xl p-5 mb-6 text-white/55 text-sm" data-testid="my-cosmetics-panel">
        Loading cosmetics…
      </div>
    );
  }

  return (
    <>
      <div className="glass rounded-3xl p-5 md:p-6 mb-6" data-testid="my-cosmetics-panel">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="font-display font-black text-xl">My Cosmetics</h2>
          <button
            onClick={() => openBrowser("skins")}
            className="btn-ghost"
            data-testid="cosmetics-browse-all-btn"
          >Browse all</button>
        </div>

        {SECTIONS.map((sec) => {
          const items = catalog[sec.key] || [];
          const owned = items.filter((it) => it.owned);
          const activeId = items.find((it) => it.active)?.id;
          // Always show at least the "default" placeholder so users see the section
          const showItems = owned.length > 0 ? owned : items.filter((it) => it.id === "default");

          return (
            <div key={sec.key} className="mb-5 last:mb-0" data-testid={`my-cosmetics-section-${sec.key}`}>
              <div className="flex items-center justify-between mb-2.5">
                <span className="label-eyebrow text-white/65" style={{ fontSize: 10 }}>{sec.label}</span>
                <span className="text-[10px] text-white/45">
                  {owned.length} owned
                </span>
              </div>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex gap-2 overflow-x-auto pb-1"
                style={{ scrollSnapType: "x proximity" }}
              >
                {showItems.map((it) => (
                  <div key={it.id} style={{ scrollSnapAlign: "start" }}>
                    <PreviewTile
                      kind={sec.kind}
                      item={it}
                      profileName={profile?.name}
                      isActive={activeId === it.id}
                      busy={busyId === it.id}
                      onClick={() => activeId !== it.id && handleEquip(sec.kind, it.id)}
                      dataTestId={`my-cosmetic-${it.id}`}
                    />
                  </div>
                ))}
                {/* "+ Get more" tile to invite browsing */}
                <button
                  onClick={() => openBrowser(sec.key)}
                  className="rounded-2xl flex flex-col items-center justify-center gap-1.5 flex-shrink-0"
                  style={{
                    minWidth: 92, height: 124,
                    background: "rgba(0,229,255,0.05)",
                    border: "1.5px dashed rgba(0,229,255,0.40)",
                    cursor: "pointer",
                  }}
                  data-testid={`my-cosmetic-browse-${sec.key}`}
                >
                  <span style={{ fontSize: 22, color: "#00E5FF" }}>+</span>
                  <span className="font-display font-bold text-[10px]" style={{ color: "#00E5FF" }}>
                    Get more
                  </span>
                </button>
              </motion.div>
            </div>
          );
        })}
      </div>

      <CosmeticsModal
        open={modalOpen}
        profile={profile}
        initialTab={modalTab}
        onClose={() => setModalOpen(false)}
        onApplied={(p) => { onProfileChange?.(p); refresh(); }}
      />
    </>
  );
}
